import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Section, Eyebrow, Headline, Lead } from '../../components/iss/IssPrimitives';
import apiService from '../../service/ApiService';
import '../initiation/PocTypePage.scss';

export type SuccessCriterion = {
  criterion: string;
  measurement: string;
};

type LocationState = {
  pocId?: string;
  pocType?: string;
  businessJustification?: Record<string, unknown>;
  scorecard?: Record<string, unknown>;
  scorecardLockMode?: 'SAVE' | 'SAVE_SUBMIT';
  facilitationSupport?: boolean;
  successCriteria?: SuccessCriterion[];
};

const emptyRow = (): SuccessCriterion => ({
  criterion: '',
  measurement: '',
});

const normalizeCriteria = (
  incoming?: SuccessCriterion[] | string[]
): SuccessCriterion[] => {
  if (!incoming?.length) return [emptyRow()];
  return incoming.map((item) =>
    typeof item === 'string'
      ? { criterion: item, measurement: '' }
      : {
          criterion: item.criterion ?? '',
          measurement: item.measurement ?? '',
        }
  );
};

const SuccessCriteriaPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state as LocationState | null) ?? {};
  const pocId = state.pocId;

  const [criteria, setCriteria] = useState<SuccessCriterion[]>(() =>
    normalizeCriteria(state.successCriteria)
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [attempted, setAttempted] = useState(false);

  const filled = criteria
    .map((item) => ({
      criterion: item.criterion.trim(),
      measurement: item.measurement.trim(),
    }))
    .filter((item) => item.criterion && item.measurement);

  const isValid = filled.length >= 1;

  const updateRow = (
    index: number,
    field: keyof SuccessCriterion,
    value: string
  ) => {
    setCriteria((current) =>
      current.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      )
    );
  };

  const addRow = () => {
    setCriteria((current) => [...current, emptyRow()]);
  };

  const removeRow = (index: number) => {
    setCriteria((current) => {
      if (current.length <= 1) return [emptyRow()];
      return current.filter((_, i) => i !== index);
    });
  };

  const handleNext = async () => {
    setAttempted(true);
    setSubmitError(null);
    if (!isValid) return;
  
    if (!pocId) {
      setSubmitError(
        'Missing PoC id. Finish Initiation submit first, then open Preparation from that flow.'
      );
      return;
    }
  
    setIsSubmitting(true);
    try {
      const saved = [];
      for (const row of filled) {
        const created = await apiService.createSuccessCriterion(pocId, {
          description: row.criterion,
          evaluationApproach: row.measurement, // temporary until UI has 3 fields
          measurementMethod: row.measurement,
        });
        saved.push({
          id: created.id,
          criterion: created.description,
          measurement: created.measurementMethod,
        });
      }
  
      navigate('/pocs/new/preparation/environment', {
        state: {
          ...state,
          successCriteria: saved,
        },
      });
    } catch (err) {
      console.error(err);
      setSubmitError(
        'Could not save success criteria. Is the PoC in Preparation? Check Network tab.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="initiation">
      <aside className="initiation__phases" aria-label="PoC phases">
        <div className="initiation__phase initiation__phase--disabled">
          <span className="initiation__phase-num">1</span>
          <span>Initiation</span>
        </div>
        <div className="initiation__phase initiation__phase--active">
          <span className="initiation__phase-num">2</span>
          <span>Preparation</span>
        </div>
        <div className="initiation__phase initiation__phase--disabled">
          <span className="initiation__phase-num">3</span>
          <span>Running</span>
        </div>
        <div className="initiation__phase initiation__phase--disabled">
          <span className="initiation__phase-num">4</span>
          <span>Evaluation</span>
        </div>
      </aside>

      <div className="initiation__main">
        <nav className="initiation__steps" aria-label="Preparation steps">
          <span className="initiation__step initiation__step--active">
            Step 1
          </span>
          <span className="initiation__step">Step 2</span>
          <span className="initiation__step">Step 3</span>
          <span className="initiation__step">Step 4</span>
          <span className="initiation__step">Step 5</span>
          <span className="initiation__step">Step 6</span>
          <span className="initiation__step">Step 7</span>
          <span className="initiation__step">Review and Submit</span>
        </nav>

        <Section theme="light" spacing="tight">
          <Eyebrow>POC Preparation</Eyebrow>
          <Headline as="h1" size="3xl">
            Define success criteria
          </Headline>
          <Lead>
            What are the defined Success criteria for your PoC? How are you
            going to evaluate it?
          </Lead>

          <p className="initiation__note">
            For each criterion, say what success looks like and how you will
            evaluate it. You will use these checks again in Evaluation.
          </p>

          <div className="sc-list">
            {criteria.map((row, index) => {
              const isEmpty =
                !row.criterion.trim() && !row.measurement.trim();
              return (
                <div key={index} className="sc-row">
                  <div className="sc-row__fields">
                    <label className="bj-field">
                      <span className="bj-field__label">
                        Success criteria {index + 1}
                      </span>
                      <textarea
                        className="bj-field__input bj-field__input--area"
                        value={row.criterion}
                        onChange={(event) =>
                          updateRow(index, 'criterion', event.target.value)
                        }
                        placeholder="e.g. Reduce cleaning time per site by 15% during the trial."
                        rows={3}
                      />
                    </label>
                    <label className="bj-field">
                      <span className="bj-field__label">
                        How are you going to evaluate it?
                      </span>
                      <textarea
                        className="bj-field__input bj-field__input--area"
                        value={row.measurement}
                        onChange={(event) =>
                          updateRow(index, 'measurement', event.target.value)
                        }
                        placeholder="e.g. Compare average minutes per clean before and after the trial."
                        rows={3}
                      />
                    </label>
                  </div>
                  <button
                    type="button"
                    className="sc-remove"
                    onClick={() => removeRow(index)}
                    disabled={criteria.length === 1 && isEmpty}
                  >
                    Remove
                  </button>
                </div>
              );
            })}
          </div>

          {attempted && !isValid ? (
            <p className="bj-field__error">
              Add at least one success criterion and how you will evaluate it
              before continuing.
            </p>
          ) : null}

          {submitError ? (
            <p className="bj-field__error">{submitError}</p>
          ) : null}

          <button type="button" className="sc-add" onClick={addRow}>
            + Add another criterion
          </button>

          <div className="initiation__actions">
            <button
              type="button"
              className="initiation__link-back"
              style={{ background: 'none', border: 'none', cursor: 'pointer' }}
              onClick={() => navigate('/pocs/new/review', { state })}
            >
              ← Back to Initiation review
            </button>

            <button
              type="button"
              className="initiation__next"
              disabled={isSubmitting}
              onClick={handleNext}
            >
              {isSubmitting ? 'Saving...' : 'Next →'}
            </button>
          </div>
        </Section>
      </div>
    </div>
  );
};

export default SuccessCriteriaPage;
